import { z } from "zod";

/**
 * Nøklene skal matche mappenavnene under /emails nøyaktig.
 */
export const EMAIL_TEMPLATE_KEYS = [
  "landing-page-inquiry",
  "meeting-agenda",
  "project-invite",
  "reset-password",
  "task-notify",
  "test-mail",
] as const;

export const ZEmailTemplateKey = z.enum(EMAIL_TEMPLATE_KEYS);
export type IEmailTemplateKey = z.infer<typeof ZEmailTemplateKey>;

/**
 * Alle filnavn vi støtter (union).
 * Vi lar dette være litt "strict" slik at vi ikke laster opp vilkårlige filer.
 */
export const EMAIL_TEMPLATE_ALLOWED_FILES = [
  "html.hbs",
  "html.hbs.json",
  "html.handlebars.json",
  "styles.css",
  "subject.html",
  "subject.pug",
  "text.pug",
  "html.pug",
] as const;

export const ZEmailTemplateFilename = z.enum(EMAIL_TEMPLATE_ALLOWED_FILES);
export type IEmailTemplateFilename = z.infer<typeof ZEmailTemplateFilename>;

/**
 * Manifest: hvilke filer som finnes/skal håndteres per template.
 * (Basert på repo-strukturen i skjermbildet.)
 */
export const EMAIL_TEMPLATE_MANIFEST: Record<IEmailTemplateKey, readonly IEmailTemplateFilename[]> = {
  "landing-page-inquiry": ["html.hbs"],
  "meeting-agenda": ["html.hbs", "html.hbs.json"],
  "project-invite": ["html.hbs", "html.handlebars.json"],
  "reset-password": ["html.hbs", "styles.css", "subject.html", "subject.pug"],
  "task-notify": ["html.hbs", "html.hbs.json"],
  "test-mail": ["html.pug"],
} as const;

/**
 * Praktisk: hvilke filer vi regner som "primær html" per template.
 * (Kan brukes av runtime/preview.)
 */
export const EMAIL_TEMPLATE_PRIMARY_HTML_FILE: Record<IEmailTemplateKey, IEmailTemplateFilename> = {
  "landing-page-inquiry": "html.hbs",
  "meeting-agenda": "html.hbs",
  "project-invite": "html.hbs",
  "reset-password": "html.hbs",
  "task-notify": "html.hbs",
  "test-mail": "html.pug",
} as const;

/**
 * Bygger "logisk path" (uavhengig av om det ligger på disk eller i Blob Storage).
 */
export const getEmailTemplatePath = (key: IEmailTemplateKey) => `emails/${key}`;

/**
 * Bygger fil-path basert på faktisk filnavn.
 */
export const getEmailTemplateFilePath = (key: IEmailTemplateKey, filename: IEmailTemplateFilename) =>
  `${getEmailTemplatePath(key)}/${filename}`;

/**
 * Gir filene som gjelder for en gitt template.
 */
export const getEmailTemplateFiles = (key: IEmailTemplateKey): readonly IEmailTemplateFilename[] =>
  EMAIL_TEMPLATE_MANIFEST[key];
